import React, { useEffect, useCallback } from 'react'
import { requestCommon, jsonResponseExtractor } from 'live/lib/utils'
import i18n from 'live/services/i18n'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'
import globalNotification from 'live/services/global-notification'

const BASE_URL = '/services/plugin-annotations/markers-query/config'

type MarkersSettings = {
    markerType: 'PLOT_VALUE' | 'STATIC'
}

const MarkersSetingsComponent: React.FC = () => {
    const [markersSettings, setMarkersSettings] = React.useState<MarkersSettings>({ markerType: 'PLOT_VALUE' })
    const { state, actions } = useWebSettingsState(markersSettings)

    const fetchItems = useCallback(() => {
        api.get()
            .then(jsonResponseExtractor)
            .then(markersSettings => {
                setMarkersSettings((markersSettings as unknown) as MarkersSettings)
                actions.setPersistedData(markersSettings)
            })
    }, [actions])

    useEffect(fetchItems, [])

    const onSave = React.useCallback(() => {
        actions.submit()

        api.persist(markersSettings)
            .then(jsonResponseExtractor)
            .then(() => {
                actions.saveSuccess(markersSettings)
                globalNotification.success(i18n('Settings successfully saved'))
            })
            .catch(async r => actions.setError(await r.text()))
    }, [actions, markersSettings])

    return (
        <WebSettingsCard
            name="export-settings"
            className="live-feature mb-15"
            open={state.open}
            error={state.submitted ? state.error : null}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => setMarkersSettings(state.persistedData)}
        >
            <div className="live-feature__header">
                <h2 className="flex-1">{i18n('Marker behavior')}</h2>
            </div>

            <div>
                <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                        {i18n('Current plot values')}:
                        <input
                            type="radio"
                            name="markerType"
                            disabled={!state.open && markersSettings.markerType !== 'PLOT_VALUE'}
                            checked={markersSettings.markerType === 'PLOT_VALUE'}
                            onChange={e =>
                                setMarkersSettings({ markerType: e.target.checked ? 'PLOT_VALUE' : 'STATIC' })
                            }
                            style={{ display: 'inline-block', marginLeft: '5px', marginTop: '2px' }}
                        />
                    </label>
                    <div className="flex">
                        <span className="help-block">
                            <span>
                                {i18n(
                                    'The marker displays the current curves and values seem on the chart for the given timestamp'
                                )}.
                            </span>
                        </span>
                    </div>
                </div>
                <div style={{ marginBottom: '35px' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                        {i18n('Original marked values')}:
                        <input
                            type="radio"
                            name="markerType"
                            disabled={!state.open && markersSettings.markerType !== 'STATIC'}
                            checked={markersSettings.markerType === 'STATIC'}
                            onChange={e =>
                                setMarkersSettings({ markerType: e.target.checked ? 'STATIC' : 'PLOT_VALUE' })
                            }
                            style={{ display: 'inline-block', marginLeft: '5px', marginTop: '2px' }}
                        />
                    </label>
                    <div className="flex">
                        <span className="help-block">
                            <span>
                                {i18n('Displays the original curves and values of when the marker was first created')}.
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </WebSettingsCard>
    )
}

const api = {
    get() {
        return fetch(BASE_URL, {
            credentials: 'same-origin'
        })
    },
    persist(settings: MarkersSettings) {
        return fetch(BASE_URL, {
            method: 'POST',
            ...requestCommon,
            body: JSON.stringify(settings)
        })
    }
}

export default {
    persist: api.persist,
    configComponent: MarkersSetingsComponent,
    section: i18n('Annotations settings'),
    name: i18n('Marker behavior')
}
